import { FormSubmitButton, FormProvider } from '@mfw/forms';
import { PageLoader, useIntl } from '@mfw/components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import React from 'react';

import type { ApiQuestionnaireSubmission } from '../../../../../schemas/questionnaireSubmissions.schema';
import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';

import {
  useQuestionnaireSubmissionForm,
  QuestionField,
} from '../../../../../allFeatures/QuestionnaireSubmissionForm';
import {
  isBlockingPendingQuestionnaire,
  usePendingQuestionnaireSubmissions,
} from '../../../../../hooks/questionnaireSubmissions';
import AppHeader from '../../../../../allFeatures/AppHeader';
import { useNavigate } from '../../../../../router/hooks';
import { useAuthContext } from '../../../../../context';
import { useWizard } from '../../../../../allFeatures';
import OnboardingHeader from '../../../OnboardingHeader';
import OnboardingFooter from '../../../OnboardingFooter';
import generalMessages from '../../../../../messages';
import pageMessages from '../../../../messages';
import Page from '../../../../Page';

const QuestionnaireSubmissionForm = ({
  submission,
}: {
  submission: ApiQuestionnaireSubmission;
}) => {
  const { formatMessage } = useIntl();
  const { handleNext, methods, questions } =
    useQuestionnaireSubmissionForm(submission);

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Page
        description={submission.questionnaire.description ?? undefined}
        title={submission.questionnaire.title}
      >
        <Page.Header>
          <AppHeader />
        </Page.Header>
        <Page.Content className="Mfw-OnboardingPage-content" withPadding>
          <div className="Mfw-OnboardingPage-screen">
            <OnboardingHeader
              description={
                submission.questionnaire.description ? (
                  <p>{submission.questionnaire.description}</p>
                ) : undefined
              }
              title={submission.questionnaire.title}
            />
            <Box flexDirection="column" display="flex" gap={2}>
              {questions.map((question, index) => (
                <QuestionField
                  question={question}
                  index={index}
                  key={question.id}
                />
              ))}
            </Box>
          </div>
        </Page.Content>
        <Page.Footer>
          <OnboardingFooter>
            <FormSubmitButton
              data-testid="onboarding-next"
              className="w-1/2 md:w-auto"
              variant="contained"
              size="large"
            >
              {formatMessage(generalMessages.next)}
            </FormSubmitButton>
          </OnboardingFooter>
        </Page.Footer>
      </Page>
    </FormProvider>
  );
};

export const PendingQuestionnairesError = ({
  onRetry,
}: {
  onRetry: () => void;
}) => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      minHeight="60vh"
      gap={2}
      p={3}
    >
      <Typography variant="h5">
        {formatMessage(pageMessages.routeErrorTitle)}
      </Typography>
      <Button
        data-testid="pending-questionnaires-retry"
        variant="contained"
        onClick={onRetry}
      >
        {formatMessage(pageMessages.routeErrorRefresh)}
      </Button>
    </Stack>
  );
};

export const PendingQuestionnairesStep = () => {
  const { isClientOnboardingRequired } = useAuthContext();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const navigate = useNavigate();
  const {
    data: submissions,
    isError,
    isLoading,
    refetch,
  } = usePendingQuestionnaireSubmissions();

  // Memoized so the redirect effect below keys off the DATA changing, not off
  // a fresh array identity per render (which re-fired it on every render).
  const blockingSubmissions = React.useMemo(
    () => submissions?.filter(isBlockingPendingQuestionnaire),
    [submissions],
  );
  const submission = blockingSubmissions?.[0];

  React.useEffect(() => {
    if (isLoading || !blockingSubmissions || blockingSubmissions.length > 0) {
      return;
    }
    // Loop-breaker: with nothing left to answer, exiting to '/' is only safe
    // when the guard will let the user stay there. An account that resolves
    // as CLIENT with `hasFinishedOnboarding` but no fitness profile (e.g. an
    // onboarded member/trainer who just accepted a CLIENT invite) still trips
    // `isClientOnboardingRequired`, and the wizard's startIndex skips the
    // fitness steps — so navigating home would bounce straight back here,
    // forever. Send those users through the fitness steps instead; completing
    // them writes the fitness profile and clears the predicate.
    if (isClientOnboardingRequired) {
      goToStep('trainingLevel');
      return;
    }
    navigate('/');
  }, [
    isLoading,
    blockingSubmissions,
    navigate,
    isClientOnboardingRequired,
    goToStep,
  ]);

  // The step is reached unconditionally at the end of the wizard, so while the
  // pending list is in flight there is nothing to render yet — without this the
  // user stares at a blank screen between the previous step and the redirect.
  if (isLoading) return <PageLoader />;
  // A failed fetch would otherwise render the same blank screen forever —
  // there is no other UI on this route to recover from.
  if (isError) return <PendingQuestionnairesError onRetry={() => refetch()} />;
  if (!submission) return null;

  return (
    <QuestionnaireSubmissionForm submission={submission} key={submission.id} />
  );
};
